import { OUTREACH_CAMPAIGN_DEFAULT_WEEKS, campaignEndsAt } from 'fym-shared/src/music24/outreach/dailyPitches';
import type { CampaignGoal } from '../hooks/useCampaignActions';

const DAY_MS = 24 * 60 * 60 * 1000;

/** The chip of a song tile (frame b1): "Best time" around the release, "Older" after 8 weeks. */
export function songTiming(releaseDate: string | null, now: Date): 'best' | 'older' | null {
  if (releaseDate === null) {
    return null;
  }
  const days = (Date.parse(releaseDate) - now.getTime()) / DAY_MS;
  if (Number.isNaN(days)) {
    return null;
  }
  if (days >= -14 && days <= 28) {
    return 'best';
  }
  return days < -OUTREACH_CAMPAIGN_DEFAULT_WEEKS * 7 ? 'older' : null;
}

/** The default goal of a song (frame b1 note): a mix when the song is out, playlists before it. */
export function defaultGoalOf(releaseDate: string | null, now: Date): CampaignGoal {
  if (releaseDate === null) {
    return 'playlists';
  }
  return Date.parse(releaseDate) <= now.getTime() ? 'mixed' : 'playlists';
}

/** 4, 8 or 12 weeks, or null ("until I stop it"). Any other stored value reads as the default. */
export function endWeeksOf(value: number | null): 4 | 8 | 12 | null {
  if (value === null) {
    return null;
  }
  return value === 4 || value === 12 ? value : 8;
}

/**
 * The last day of a campaign (frame b1 "Runs until"): the stored end of a started campaign, else
 * the end it gets when it starts now. Null: "until I stop it".
 */
export function runsUntilOf(
  campaign: { endsAt: string | null; endWeeks: number | null; releaseDate: string | null },
  now: Date
): Date | null {
  if (campaign.endsAt !== null) {
    return new Date(campaign.endsAt);
  }
  return campaignEndsAt({ releaseDate: campaign.releaseDate, startedAt: now, weeks: endWeeksOf(campaign.endWeeks) });
}

/**
 * About how many people Plug pitches before the end (frame c1 "Up to 280 people"): the weekdays
 * from tomorrow to the end day, times the daily rate. Null with no end day.
 */
export function pitchesUntil(endsAt: Date | null, rate: number, now: Date): number | null {
  if (endsAt === null) {
    return null;
  }
  let weekdays = 0;
  for (let time = now.getTime() + DAY_MS; time <= endsAt.getTime(); time += DAY_MS) {
    const day = new Date(time).getUTCDay();
    if (day !== 0 && day !== 6) {
      weekdays += 1;
    }
  }
  return weekdays * rate;
}
